import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArrowLeft, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { resultsEntryPageTitle } from '../../utils/examResultsLabels';
import useBranchLabels from '../../hooks/useBranchLabels';

const ExamResultsEntry = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();

  const [exam, setExam] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [marks, setMarks] = useState({});
  const [remarks, setRemarks] = useState({});
  const [finalRemarks, setFinalRemarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const loadExam = async () => {
      try {
        const res = await api.get(`/exams/${examId}`);
        setExam(res.data.data);
        const dsRes = await api.get(`/exams/${examId}/datesheets`);
        const dsList = dsRes.data.data || [];
        const cls = dsList.map((d) => ({ id: d.class_id, name: d.class_name }));
        setClasses(cls);
        if (cls.length > 0) setSelectedClass(String(cls[0].id));
      } catch {
        toast({ title: 'Error', description: 'Failed to load exam', variant: 'destructive' });
      }
    };
    loadExam();
  }, [examId, toast]);

  const fetchMatrix = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        class_id: selectedClass,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const res = await api.get(`/exams/${examId}/results/matrix`, { params });
      const data = res.data.data || {};
      setStudents(data.students || []);
      setSchedules(data.schedules || []);
      const initial = {};
      const initialRemarks = {};
      Object.entries(data.marks || {}).forEach(([key, val]) => {
        initial[key] = val.marks_obtained ?? '';
        initialRemarks[key] = val.remarks ?? '';
      });
      setMarks(initial);
      setRemarks(initialRemarks);
      const fr = {};
      (data.students || []).forEach((st) => {
        fr[st.id] = st.report_card_remarks ?? '';
      });
      setFinalRemarks(fr);
    } catch {
      toast({ title: 'Error', description: 'Failed to load results matrix', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [examId, selectedClass, user?.school_id, toast]);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const markKey = (studentId, scheduleId) => `${studentId}_${scheduleId}`;

  const setMark = (studentId, scheduleId, value) => {
    setMarks((prev) => ({ ...prev, [markKey(studentId, scheduleId)]: value }));
  };

  const setRemark = (studentId, scheduleId, value) => {
    setRemarks((prev) => ({ ...prev, [markKey(studentId, scheduleId)]: value }));
  };

  const setFinalRemark = (studentId, value) => {
    setFinalRemarks((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleSave = async () => {
    const entries = [];
    students.forEach((st) => {
      schedules.forEach((sch) => {
        const val = marks[markKey(st.id, sch.id)];
        const rem = remarks[markKey(st.id, sch.id)];
        if (val !== '' && val !== null && val !== undefined) {
          entries.push({
            exam_schedule_id: sch.id,
            student_id: st.id,
            marks_obtained: Number(val),
            remarks: rem ?? null,
          });
        }
      });
    });
    if (entries.length === 0) {
      toast({ title: 'Error', description: 'Enter at least one mark', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/exams/${examId}/results`, { class_id: Number(selectedClass), entries, final_remarks: finalRemarks });
      toast({ title: 'Success', description: 'Results saved as draft' });
      fetchMatrix();
    } catch {
      toast({ title: 'Error', description: 'Failed to save results', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="exam-results-entry">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/exams/${examId}`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{resultsEntryPageTitle(exam)}</h1>
          <p className="text-muted-foreground">{exam?.name || 'Exam'}</p>
        </div>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-lg">Marks by class</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs">
            <Label>Class</Label>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent className="bg-muted border-border">
                {classes.map((c) => <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
          ) : schedules.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">No schedules for this class.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[140px]">Student</TableHead>
                      {schedules.map((sch) => (
                        <TableHead key={sch.id} className="min-w-[90px] text-center">
                          {sch.subject_name}
                          <span className="block text-xs font-normal text-muted-foreground">/{sch.max_marks}</span>
                        </TableHead>
                      ))}
                      <TableHead className="min-w-[220px]">Final Remark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((st) => (
                      <TableRow key={st.id}>
                        <TableCell className="font-medium">
                          {st.first_name} {st.last_name}
                          <span className="block text-xs text-muted-foreground">{st.admission_no}</span>
                        </TableCell>
                        {schedules.map((sch) => (
                          <TableCell key={sch.id} className="p-1">
                            <div className="space-y-1">
                              <Input
                                type="number"
                                min={0}
                                max={sch.max_marks}
                                value={marks[markKey(st.id, sch.id)] ?? ''}
                                onChange={(e) => setMark(st.id, sch.id, e.target.value)}
                                className="bg-muted border-border h-9 text-center w-20 mx-auto"
                              />
                              <Input
                                value={remarks[markKey(st.id, sch.id)] ?? ''}
                                onChange={(e) => setRemark(st.id, sch.id, e.target.value)}
                                placeholder="Remark"
                                className="bg-muted border-border h-8 text-xs w-24 mx-auto"
                              />
                            </div>
                          </TableCell>
                        ))}
                        <TableCell className="p-1">
                          <Input
                            value={finalRemarks[st.id] ?? ''}
                            onChange={(e) => setFinalRemark(st.id, e.target.value)}
                            placeholder="Final remark"
                            className="bg-muted border-border h-9"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-2 pt-2">
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving…' : 'Save draft'}
                </Button>
                <Button variant="outline" onClick={() => navigate(`/exams/${examId}`)}>
                  Back to exam
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExamResultsEntry;
